import { useState, useEffect } from 'react';
import { 
    Modal, 
    Form, 
    Select, 
    Input, 
    Button, 
    Space, 
    message, 
    Alert,
    Card,
    Row,
    Col,
    Typography,
    Tag,
    Divider } from 'antd';
import { 
    UserOutlined, 
    SwapOutlined,
    HistoryOutlined,
    TrophyOutlined} from '@ant-design/icons';
import apiClient from '../utils/apiClient';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const ReassignQueryModal = ({ visible, onClose, selectedQueries, onSuccess }) => {
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [userPerformance, setUserPerformance] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        if (visible) {
            fetchUsers();
            if (selectedQueries.length === 1) {
                fetchReassignmentHistory(selectedQueries[0]._id)
            }
        }
    }, [visible, selectedQueries]);

    const fetchUsers = async () => {
        try {
            const response = await apiClient.get('/teams/assignable-users');
            const userList = response.data?.data || response.data?.users || response.data || [];
            setUsers(userList);
            fetchUserPerformance(userList);
        } catch (error) {
            try {
                const response = await apiClient.get('/users');
                const userList = response.data?.users || response.data?.data || response.data || [];
                setUsers(userList);
                fetchUserPerformance(userList);
            } catch (err) {
                console.error('Error fetching users:', error);
                message.error('Failed to load users');
            }
        }
    };

    const fetchUserPerformance = async (userList) => {
        try {
            const performancePromises = userList.map(async (u) => {
                try {
                    const response = await apiClient.get(`/users/${u._id}/performance`);
                    return { userId: u._id, performance: response.data }
                } catch (error) {
                    return { userId: u._id, performance: null }
                }
            });

            const performanceResults = await Promise.all(performancePromises);
            const performanceMap = {};
            performanceResults.forEach(result => {
                performanceMap[result.userId] = result.performance
            });
            setUserPerformance(performanceMap)
        } catch (error) {
            console.error('Error fetching user performance:', error)
        }
    };

    const fetchReassignmentHistory = async (leadId) => {
        try {
            const response = await apiClient.get(`/leads/${leadId}/reassignment-history`);
            // Handle reassignment history if needed
        } catch (error) {
            console.error('Error fetching reassignment history:', error)
        }
    };

    const handleReassign = async (values) => {
        try {
            setLoading(true);
            
            if (selectedQueries.length === 1) {
                // Single reassignment;
                const response = await apiClient.put(`/leads/${selectedQueries[0]._id}/reassign`, {
                    assignedTo: values.assignedTo,
                    reason: values.reason
                });
                
                if (response.data.success) {
                    message.success('Query reassigned successfully');
                    onSuccess(response.data.lead)
                }
            } else {
                // Bulk reassignment;
                const leadIds = selectedQueries.map(q => q._id);
                const response = await apiClient.put('/leads/bulk-reassign', {
                    leadIds: leadIds,
                    assignedTo: values.assignedTo,
                    reason: values.reason
                });
                
                if (response.data.success) {
                    message.success(`${response.data.reassignedCount} queries reassigned successfully`);
                    onSuccess()
                }
            }
            
            form.resetFields();
            onClose()
        } catch (error) {
            console.error('Error reassigning queries:', error);
            message.error('Failed to reassign queries')
        } finally {
            setLoading(false)
        }
    };

    const getUserPerformanceDisplay = (userId) => {
        const performance = userPerformance[userId];
        if (!performance) return null;

        return (
            <div style={{ marginTop: 8, fontSize: '12px' }}>
                <Row gutter={8}>
                    <Col span={8}>
                        <Text type="secondary">Conversion: </Text>
                        <Text strong>{(performance.conversionRate * 100).toFixed(1)}%</Text>
                    </Col>
                    <Col span={8}>
                        <Text type="secondary">Avg Deal: </Text>
                        <Text strong>${performance.averageDealValue?.toLocaleString()}</Text>
                    </Col>
                    <Col span={8}>
                        <Text type="secondary">Rating: </Text>
                        <Text strong>{performance.customerSatisfaction?.toFixed(1)}/5</Text>
                    </Col>
                </Row>
                {performance.internationalConversionRate > 0.1 && (
                    <Tag color="gold" size="small" style={{ marginTop: 4 }}>
                        <TrophyOutlined /> International Expert;
                    </Tag>
                )}
            </div>
        )
    };

    const getDestinationExpertise = (userId) => {
        const performance = userPerformance[userId];
        if (!performance?.destinationPerformance) return null;

        const topDestinations = (performance.destinationPerformance || [])
            .filter(dp => dp.conversionRate > 0.15 && dp.totalLeads >= 3)
            .sort((a, b) => b.conversionRate - a.conversionRate)
            .slice(0, 3);

        if (topDestinations.length === 0) return null;

        return (
            <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>Expert in: </Text>
                {topDestinations.map((dest, index) => (
                    <Tag key={index} size="small" color="blue">
                        {dest.destination} ({(dest.conversionRate * 100).toFixed(0)}%)
                    </Tag>
                ))}
            </div>
        )
    };

    return (
        <Modal
            title={
                <Space>
                    <SwapOutlined />
                    {selectedQueries.length === 1 ? 'Reassign Query' : `Reassign ${selectedQueries.length} Queries`}
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            <Form form={form} layout="vertical" onFinish={handleReassign}>
                {selectedQueries.length === 1 && (
                    <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
                        <Title level={5} style={{ margin: 0 }}>
                            {selectedQueries[0].customerName || selectedQueries[0].clientName}
                        </Title>
                        <Text type="secondary">
                            {selectedQueries[0].destination} • {selectedQueries[0].email}
                        </Text>
                        <br />
                        <Text type="secondary">
                            Currently assigned to: <Text strong>{selectedQueries[0].assignedTo?.firstName} {selectedQueries[0].assignedTo?.lastName}</Text>
                        </Text>
                    </Card>
                )}

                {selectedQueries.length > 1 && (
                    <Alert
                        message={`Bulk Reassignment`}
                        description={`You are about to reassign ${selectedQueries.length} queries to a new team member.`}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item
                    name="assignedTo"
                    label="Assign to User"
                    rules={[{ required: true, message: 'Please select a user' }]}
                >
                    <Select
                        placeholder="Select user to assign queries"
                        showSearch
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={setSelectedUser}
                    >
                        {users.map(u => (
                            <Option key={u._id} value={u._id}>
                                <div>
                                    <div>
                                        <UserOutlined /> {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                                        <Tag color="blue" size="small" style={{ marginLeft: 8 }}>
                                            {u.role}
                                        </Tag>
                                    </div>
                                    {getUserPerformanceDisplay(u._id)}
                                    {getDestinationExpertise(u._id)}
                                </div>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="reason"
                    label="Reason for Reassignment"
                    rules={[{ required: true, message: 'Please provide a reason' }]}
                >
                    <TextArea
                        rows={3}
                        placeholder="Enter reason for reassignment (e.g., workload balancing, expertise match, etc.)"
                    />
                </Form.Item>

                <Divider />

                <Form.Item style={{ marginBottom: 0 }}>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {selectedQueries.length === 1 ? 'Reassign Query' : `Reassign ${selectedQueries.length} Queries`}
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                        {selectedQueries.length === 1 && (
                            <Button
                                type="link" 
                                icon={<HistoryOutlined />}
                                onClick={() => {
                                    // Show reassignment history modal
                                }}
                            >
                                View History;
                            </Button>
                        )}
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    )
};

export default ReassignQueryModal;